import { orgConfigState, useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { sortBy, reverse, throttle } from 'lodash-es';
import { useCallback } from 'react';
import { atom, useSetAtom, useAtomValue } from 'jotai';
import { shopeeDomainScoreCache } from 'services/cache';

const shopeeDomains = [
  's-cf-ph.shopeesz.com',
  's-cf-id.shopeesz.com',
  'origin', // 取到 origin 时，应该用接口给的 url。
  's-cf-my.shopeesz.com',
  's-cf-sg.shopeesz.com',
  's-cf-tw.shopeesz.com',
  's-cf-th.shopeesz.com',
  's-cf-vn.shopeesz.com',
  's-cf-br.shopeesz.com',
];

const updateCache = throttle((next: any) => {
  shopeeDomainScoreCache.set(next);
}, 5000);

export interface DomainScore {
  domain: string;
  score: number;
}

const defaultShopeeDomainScore = shopeeDomains.map<DomainScore>((domain, index) => {
  return {
    domain,
    score: Number((1 - (index + 1) / 10).toFixed(1)),
  };
});

const ShopeeDomainScoreAtom = atom(
  shopeeDomainScoreCache.get<DomainScore[]>() || defaultShopeeDomainScore,
);
export const ShopeeDomainScore = atom(
  (get) => get(ShopeeDomainScoreAtom),
  (get, set, next: DomainScore[] | ((prev: DomainScore[]) => DomainScore[])) => {
    const prev = get(ShopeeDomainScoreAtom);
    const nextVal = typeof next === 'function' ? next(prev) : next;
    set(ShopeeDomainScoreAtom, nextVal);
    updateCache(nextVal);
  },
);

export const SortedShopeeDomains = atom((get) => {
  const { shopeeImageDomainScoreEnable } = get(orgConfigState);

  if (!shopeeImageDomainScoreEnable) {
    return defaultShopeeDomainScore;
  }

  const shopeeDomainScore = get(ShopeeDomainScore);

  return reverse(sortBy(shopeeDomainScore, 'score'));
});

export const useSortedShopeeDomains = () => {
  return useAtomValue(SortedShopeeDomains);
};

export const useUpdateShopeeDomainScore = () => {
  const setShopeeDomainScore = useSetAtom(ShopeeDomainScore);

  const { shopeeImageDomainScoreEnable } = useOrgConfigStateValue();

  const updateShopeeDomainScore = useCallback(
    (domain: string, score: number) => {
      if (!shopeeImageDomainScoreEnable) {
        return;
      }

      setShopeeDomainScore((prev) => {
        return prev.map((_) => {
          if (_.domain !== domain) {
            return _;
          }
          return {
            domain,
            score: _.score + score,
          };
        });
      });
    },
    [shopeeImageDomainScoreEnable, setShopeeDomainScore],
  );

  return updateShopeeDomainScore;
};
